import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import { 
  Briefcase, 
  Upload, 
  Clock, 
  Image as ImageIcon,
  Save,
  Eye,
  X
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const DEFAULT_FORM_DATA = {
  kicker: 'Careers',
  title: 'Careers',
  summary: 'Join our mission to nurture learners with character, competence, and compassion.',
  slides: [
    { image: '/assets/news/news1.jpg', order: 1, caption: 'Teaching Excellence' },
    { image: '/assets/news/news5.jpg', order: 2, caption: 'Collaborative Team' },
    { image: '/assets/news/news8.jpg', order: 3, caption: 'Purpose-Driven Work' }
  ]
};

export default function CareersHeroForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    summary: false
  });
  const [pendingSlides, setPendingSlides] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  const getAuthHeaders = useCallback(() => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }, []);

  const fetchMediaMap = useCallback(async () => {
    try {
      const cached = sessionStorage.getItem('cms_media_map');
      if (cached) {
        setMediaMap(JSON.parse(cached));
        return;
      }
      const res = await fetch(`${apiBase}/media?limit=500`, { 
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const json = await res.json();
        const items = json.data?.items || json.data || [];
        if (Array.isArray(items)) {
          const map = {};
          items.forEach(m => {
            if (m.id && m.file_path) {
              map[m.id] = m.file_path;
              map[String(m.id)] = m.file_path;
            }
          });
          setMediaMap(map);
          try {
            sessionStorage.setItem('cms_media_map', JSON.stringify(map));
          } catch (e) {}
        }
      }
    } catch (e) {
      console.warn('[CareersHeroForm] Media map fetch error:', e);
    }
  }, [apiBase, getAuthHeaders]);

  const resolveImage = useCallback((imgSrc) => {
    if (!imgSrc) return null;
    const str = String(imgSrc).trim();
    if (!str) return null;
    if (mediaMap[str]) return mediaMap[str];
    const num = Number(str);
    if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
    const resolved = resolveMediaSrc(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${str}`;
    }
    return str;
  }, [mediaMap, apiBase]);

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=careers`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'careers-hero' || s.section_type === 'hero');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=careers`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => s.section_type === 'careers-hero' || s.section_type === 'hero');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const rawSlides = Array.isArray(en.slides) && en.slides.length > 0 ? en.slides.slice(0, 3) : DEFAULT_FORM_DATA.slides;
        
        // Ensure exactly 3 slides with order 1, 2, 3
        const normalizedSlides = [0, 1, 2].map(idx => {
          const existing = rawSlides[idx] || {};
          return {
            image: existing.image || '',
            order: idx + 1,
            caption: existing.caption || `Slide ${idx + 1}`
          };
        });

        const loaded = {
          kicker: en.kicker || 'Careers',
          title: en.title || 'Careers',
          summary: en.summary || en.subtitle || DEFAULT_FORM_DATA.summary,
          slides: normalizedSlides
        };

        setFormData(loaded);
        setOriginalData(loaded);

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              const pFields = { kicker: false, title: false, summary: false };
              const pSlides = {};

              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  if (['kicker', 'title', 'summary'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                  } else if (c.field_id?.includes('slides.')) {
                    const m = c.field_id.match(/slides\.(\d+)/);
                    if (m) pSlides[`slide_${m[1]}`] = true;
                  }
                }
              });

              setPendingFields(pFields);
              setPendingSlides(pSlides);
            }
          }
        } catch (pe) {
          console.warn('[CareersHeroForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[CareersHeroForm] Load error:', err);
      toast.error('Gagal memuat data Careers Hero');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    fetchMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSlideChange = (index, field, value) => {
    setFormData(prev => {
      const next = [...prev.slides];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, slides: next };
    });
  };

  const handleImageUpload = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran foto maksimal 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Format foto harus JPG, PNG, atau WebP');
      return;
    }

    setImageUploading(prev => ({ ...prev, [index]: true }));
    setUploadProgress(prev => ({ ...prev, [index]: 10 }));

    const uploadData = new FormData();
    uploadData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (err) {}
    }
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setUploadProgress(prev => ({ ...prev, [index]: pct }));
      }
    };

    xhr.onload = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          let mediaUrl = data.url || data.file_path || data.file_url || res.url;
          if (mediaUrl && !mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
            mediaUrl = `${origin}${mediaUrl.startsWith('/') ? '' : '/'}${mediaUrl}`;
          }
          if (mediaUrl) {
            handleSlideChange(index, 'image', mediaUrl);
            toast.success(`Foto Slide ${index + 1} berhasil diunggah. Klik "Save Slide" untuk mengajukan.`);
          } else {
            toast.error('Gagal membaca URL foto');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Upload gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      toast.error('Terjadi gangguan jaringan saat upload');
    };

    xhr.send(uploadData);
  };

  const handleSubmitField = async (field, customValue, fieldDisplayName) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const valToSubmit = customValue !== undefined ? customValue : formData[field];
    const oldVal = originalData[field];

    setSubmitting(prev => ({ ...prev, [field]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: fieldDisplayName || (field.charAt(0).toUpperCase() + field.slice(1)),
          old_value: oldVal,
          new_value: valToSubmit,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${fieldDisplayName || field}" berhasil diajukan untuk approval`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: valToSubmit }));
        try {
          sessionStorage.removeItem('cms_page_careers');
          sessionStorage.removeItem('cms_sections_careers');
          window.dispatchEvent(new Event('cms-careers-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[CareersHeroForm] Submit error:', err);
      toast.error('Error submitting change: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmitSlide = async (index) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const slide = formData.slides[index];
    const key = `slide_${index}`;

    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `slides.${index}.update`,
          field_name: `Hero Slide ${index + 1}`,
          old_value: originalData.slides?.[index] || {},
          new_value: slide,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Hero Slide ${index + 1} berhasil diajukan untuk approval`);
        setPendingSlides(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.slides || [])];
          next[index] = slide;
          return { ...prev, slides: next };
        });
        try {
          sessionStorage.removeItem('cms_page_careers');
          sessionStorage.removeItem('cms_sections_careers');
          window.dispatchEvent(new Event('cms-careers-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[CareersHeroForm] Submit slide error:', err);
      toast.error('Error submitting slide: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Careers Hero Section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Briefcase size={22} />
        </div>
        <div>
          <h2 className="unified-title">Careers Hero Section</h2>
          <p className="unified-subtitle">
            Kelola judul utama, ringkasan misi, dan slideshow foto carousel pada bagian header halaman Careers (Maksimal 3 foto - Hanya Replace Image).
          </p>
        </div>
      </div>

      {/* Section Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker}
            placeholder="e.g. Careers"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker', formData.kicker, 'Section Kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Hero Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Hero Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title}
            placeholder="e.g. Careers"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', formData.title, 'Hero Title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Mission Summary */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Mission Summary & Subtitle</label>
          {pendingFields.summary && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <textarea
            className="unified-textarea"
            rows={2}
            value={formData.summary}
            onChange={(e) => handleFieldChange('summary', e.target.value)}
            disabled={pendingFields.summary}
            placeholder="Join our mission to nurture learners with character, competence, and compassion."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('summary', formData.summary, 'Mission Summary')}
            disabled={pendingFields.summary || submitting.summary || formData.summary === originalData.summary}
          >
            <Save size={14} /> {submitting.summary ? 'Saving...' : (pendingFields.summary ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Hero Slideshow Manager (Exactly 3 slides, Replace Only) */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label">Hero Slideshow Carousel (3 Slides)</label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Foto-foto ini akan membentuk background carousel hero di bagian paling atas halaman Careers (Maksimal 3 Foto - Hanya Ganti Foto).
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', marginTop: '16px' }}>
          {formData.slides.map((slide, idx) => {
            const isPending = pendingSlides[`slide_${idx}`];
            const isSubmitting = submitting[`slide_${idx}`];
            const isUploading = imageUploading[idx];
            const progress = uploadProgress[idx] || 0;
            const resolvedImg = resolveImage(slide.image);

            return (
              <div 
                key={idx}
                style={{
                  border: isPending ? '1.5px dashed #f59e0b' : '1px solid #e2e8f0',
                  borderRadius: '14px',
                  overflow: 'hidden',
                  background: isPending ? '#fffdf7' : '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {/* Media Preview Box */}
                <div style={{ position: 'relative', height: '170px', background: '#0f172a' }}>
                  {resolvedImg ? (
                    <img 
                      src={resolvedImg} 
                      alt={`Slide ${idx + 1}`} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} 
                      onClick={() => setLightboxImage(resolvedImg)}
                      title="Klik untuk memperbesar foto"
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                      <ImageIcon size={32} />
                      <span style={{ fontSize: '12px', marginTop: '6px' }}>Belum ada foto</span>
                    </div>
                  )}

                  {/* Lightbox Trigger if image exists */}
                  {resolvedImg && (
                    <button
                      type="button"
                      onClick={() => setLightboxImage(resolvedImg)}
                      style={{
                        position: 'absolute',
                        bottom: '10px',
                        left: '10px',
                        background: 'rgba(15, 23, 42, 0.75)',
                        color: '#ffffff',
                        border: 'none',
                        padding: '6px 10px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '11px'
                      }}
                      title="Lihat Pratinjau Foto"
                    >
                      <Eye size={12} />
                      <span>Lihat</span>
                    </button>
                  )}

                  {/* Upload overlay / button */}
                  <label 
                    style={{
                      position: 'absolute',
                      bottom: '10px',
                      right: '10px',
                      background: 'rgba(15, 23, 42, 0.85)',
                      color: '#ffffff',
                      padding: '6px 12px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      backdropFilter: 'blur(4px)'
                    }}
                  >
                    <Upload size={13} />
                    <span>{isUploading ? `${progress}%` : (resolvedImg ? 'Ganti Foto' : 'Pilih Foto')}</span>
                    <input 
                      type="file" 
                      accept="image/jpeg,image/png,image/webp" 
                      style={{ display: 'none' }}
                      disabled={isPending || isUploading}
                      onChange={(e) => handleImageUpload(idx, e)}
                    />
                  </label>

                  {/* Badge Slide Number */}
                  <div style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(15, 23, 42, 0.8)', color: '#ffffff', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>
                    SLIDE #{idx + 1}
                  </div>

                  {/* Pending Badge */}
                  {isPending && (
                    <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                      <span className="unified-pending-pill" style={{ background: '#fef3c7', color: '#92400e' }}>
                        <Clock size={11} /> Pending
                      </span>
                    </div>
                  )}
                </div>

                {/* Caption & Controls Body */}
                <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                      Caption Slide #{idx + 1}
                    </label>
                  </div>

                  <input 
                    type="text"
                    className="unified-input"
                    value={slide.caption || ''}
                    disabled={isPending}
                    onChange={(e) => handleSlideChange(idx, 'caption', e.target.value)}
                    placeholder="e.g. Teaching Excellence"
                    style={{ fontSize: '13px', padding: '8px 12px' }}
                  />

                  {/* Submit button */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                    <button
                      type="button"
                      className="unified-submit-button"
                      onClick={() => handleSubmitSlide(idx)}
                      disabled={isPending || isSubmitting}
                      style={{ padding: '6px 14px', fontSize: '12px' }}
                    >
                      <Save size={13} />
                      {isSubmitting ? 'Saving...' : (isPending ? 'Pending' : 'Save Slide')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          onClick={() => setLightboxImage(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              background: '#0f172a',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
          >
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'rgba(0, 0, 0, 0.6)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10
              }}
            >
              <X size={20} />
            </button>
            <img 
              src={lightboxImage} 
              alt="Preview" 
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
